import { ICustomImageCropProps } from '@app/models/shared.models';
import Injector from '@app/services';
import { IntlService } from '@app/services/intl.service';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Icon,
} from '@material-ui/core';
import React from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { default as FontIcon } from './icon.component';

let originalImageRef: HTMLImageElement;
let canvasRef: string;
let originalFileName: string;
const intlService: IntlService = Injector.get(IntlService);
let rotationDirection: number;
// 2 for 180Deg
// 1 for Right
// 0 for Left

const CropImage = (props: ICustomImageCropProps) => {
	originalFileName = props.fileName;

	// Functional component state management

	// Start region
	const reactCropInitState: ReactCrop.Crop = {
		unit: '%',
		width: 70,
		aspect: 1,
	};
	const [crop, setCrop] = React.useState(reactCropInitState);
	const [onLoading, setOnLoading] = React.useState(false);
	const [rotate, setRotate] = React.useState(0);
	const [rotateClick, setRotateClick] = React.useState(0);
	const [imgSrc, setImgSrc] = React.useState('');
	// End region

	const defaultDialogConfirmText = intlService.GetFormattedMessage('app.common.dialog-confirm-button-text');
	const defaultDialogCancelText = intlService.GetFormattedMessage('app.common.dialog-cancel-button-text');
	const defaultDialogPreviewText = intlService.GetFormattedMessage('app.common.dialog-preview-image-text');

	const closeImagePreview = (e: React.MouseEvent) => {
		props.onCropCancel(false);
		setCrop(reactCropInitState);
		setImgSrc(null);
	};
	const onCropComplete = (cropAmount: ReactCrop.Crop, percentCrop?: ReactCrop.PercentCrop) => {
		if (originalImageRef && cropAmount.width && cropAmount.height) {
			getCroppedImage(originalImageRef, cropAmount);
		}
	};

	const onCropChange = (cropAmount: ReactCrop.Crop, percentCrop: ReactCrop.PercentCrop) => {
		setCrop(cropAmount);
	};

	const handleConfirmCropClick = (e: React.MouseEvent, callback: (file: File, err?: any) => void) => {
		setOnLoading(true);
		const file = base64StringtoFile(canvasRef, originalFileName);
		callback(file);
	};

	const rotateImage = (value: number) => {
		switch (value) {
			case 0:
				setRotate(0);
				break;
			// Left Rotate
			case 1:
				setRotate(90);
				break;
			case 2:
				setRotate(180);
				break;
			// Right Rotate
			case 3:
				setRotate(-90);
				break;
			default:
				setRotate(0);
				break;
		}
	};

	React.useEffect(() => {
		if (!props.isOpen) {
			const cleanUpPromise = new Promise((resolve, reject) => {
				setTimeout(() => resolve(true), 2000);
			});
			cleanUpPromise.then((res) => {
				if (res) {
					setOnLoading(false);
					setCrop(reactCropInitState);
					setImgSrc(null);
				}
			});
		} else {
			setCrop(reactCropInitState);
		}
	}, [props.isOpen]);

	React.useEffect(() => {
		rotateImage(rotateClick);
	}, [rotateClick]);

	React.useEffect(() => {
		const img = new Image();
		img.src = props.imageSrc.toString();
		img.onload = () => {
			originalImageRef = img;
		};
	}, [props.imageSrc]);

	React.useEffect(() => {
		if (!!originalImageRef) {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			switch (rotate) {
				case 0:
					setImgSrc(props.imageSrc.toString());
					break;
				case -90:
					canvas.width = originalImageRef.height;
					canvas.height = originalImageRef.width;
					// if image is at 180deg then left rotate the img
					if (rotationDirection === 2) {
						rotationDirection = 1;
						ctx.transform(0, -1, 1, 0, 0, originalImageRef.width);
						ctx.drawImage(originalImageRef, 0, 0);
						setImgSrc(canvas.toDataURL().toString());
						break;
					}
					rotationDirection = 1;
					ctx.transform(0, 1, -1, 0, originalImageRef.height, 0);
					ctx.drawImage(originalImageRef, 0, 0);
					setImgSrc(canvas.toDataURL().toString());
					break;
				case 180:
					canvas.width = originalImageRef.height;
					canvas.height = originalImageRef.width;
					// if image is right rotated then right rotate the img
					// else left rotate the img
					if (rotationDirection) {
						ctx.transform(0, 1, -1, 0, originalImageRef.height, 0);
					} else {
						ctx.transform(0, -1, 1, 0, 0, originalImageRef.width);
					}
					ctx.drawImage(originalImageRef, 0, 0);
					rotationDirection = 2;
					setImgSrc(canvas.toDataURL().toString());
					break;
				case 90:
					canvas.width = originalImageRef.height;
					canvas.height = originalImageRef.width;
					// if image is at 180deg then right rotate the img
					if (rotationDirection === 2) {
						rotationDirection = 0;
						ctx.transform(0, 1, -1, 0, originalImageRef.height, 0);
						ctx.drawImage(originalImageRef, 0, 0);
						setImgSrc(canvas.toDataURL().toString());
						break;
					}
					rotationDirection = 0;
					ctx.transform(0, -1, 1, 0, 0, originalImageRef.width);
					ctx.drawImage(originalImageRef, 0, 0);
					setImgSrc(canvas.toDataURL().toString());
				default:
					break;
			}
		}
		onCropComplete(crop);
	}, [rotate]);

	const getCroppedImage = (image: HTMLImageElement, cropAmount: ReactCrop.Crop) => {
		const canvas = document.createElement('canvas');
		const scaleX = image.naturalWidth / image.width;
		const scaleY = image.naturalHeight / image.height;
		const ctx = canvas.getContext('2d');
		canvas.width = cropAmount.width;
		canvas.height = cropAmount.height;
		ctx.drawImage(
			image,
			cropAmount.x * scaleX,
			cropAmount.y * scaleY,
			cropAmount.width * scaleX,
			cropAmount.height * scaleY,
			0,
			0,
			cropAmount.width,
			cropAmount.height
		);
		canvasRef = canvas.toDataURL('image/jpeg', 0.7);
	};

	const base64StringtoFile = (base64String: string, fileName: string) => {
		const arr = base64String.split(',');
		// Used to extract the mime type of the image
		const blobstr = atob(arr[1]);
		let n = blobstr.length;
		const u8arr = new Uint8Array(n);
		while (n--) {
			u8arr[n] = blobstr.charCodeAt(n);
		}
		return new File([u8arr], fileName, { type: 'image/jpeg' });
	};

	return (
		<Dialog open={props.isOpen}>
			<DialogTitle className='text-center'>{defaultDialogPreviewText}</DialogTitle>
			<Grid container={true} justifyContent='center'>
				<Icon
					onClick={() => {
						if (rotateClick !== 3) {
							setRotateClick(rotateClick + 1);
						} else {
							setRotateClick(0);
						}
					}}
				>
					<FontIcon name='arrow-anti-clockwise' />
				</Icon>
				<Icon
					onClick={() => {
						if (rotateClick !== 0) {
							setRotateClick(rotateClick - 1);
						} else {
							setRotateClick(3);
						}
					}}
				>
					<FontIcon name='arrow-clockwise' />
				</Icon>
			</Grid>
			<DialogContent>
				{props.imageSrc && (
					<ReactCrop
						src={!!imgSrc ? imgSrc : props.imageSrc.toString()}
						crop={crop}
						onImageLoaded={(image) => (originalImageRef = image)}
						onComplete={onCropComplete}
						onChange={onCropChange}
					/>
				)}
			</DialogContent>
			<DialogActions>
				<Button color='primary' variant='outlined' onClick={closeImagePreview}>
					{defaultDialogCancelText}
				</Button>
				<Button
					color='secondary'
					onClick={(e: React.MouseEvent) => handleConfirmCropClick(e, props.uploadFileCallback)}
					disabled={onLoading}
				>
					{defaultDialogConfirmText}
					{onLoading && <CircularProgress size={16} className='ml-1' />}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CropImage;



// WEBPACK FOOTER //
// ./src/components/shared/crop-image.component.tsx